/**
 * AxLabs Physics Experiments - Thermodynamics
 *
 * Collection of thermodynamics experiments generated using
 * the declarative framework.
 */
import { createExperimentFromSchema, IDEAL_GAS_CONSTANT } from '../../framework/index.js';
import { getParam, getState, getStateValues } from './helpers.js';
// ============================================================================
// IDEAL GAS ISOTHERMAL PROCESS
// ============================================================================
export const IdealGasIsothermal = createExperimentFromSchema({
    id: 'ideal-gas-isothermal',
    name: 'Ideal Gas - Isothermal Process',
    domain: 'physics',
    description: 'Ideal gas undergoing isothermal (constant temperature) expansion/compression',
    conceptTags: ['Ideal Gas Law', 'Isothermal Process', 'Boyle\'s Law'],
    difficulty: 4,
    stateVariables: [
        { id: 'V', name: 'Volume', unit: 'm³', initial: 0.01, symbol: 'V' },
        { id: 'P', name: 'Pressure', unit: 'Pa', initial: 0, symbol: 'P' },
        { id: 'work', name: 'Work Done', unit: 'J', initial: 0, symbol: 'W' },
    ],
    parameters: [
        { id: 'n', name: 'Moles', unit: 'mol', min: 0.01, max: 10, default: 0.1, symbol: 'n' },
        { id: 'T', name: 'Temperature', unit: 'K', min: 100, max: 1000, default: 300, symbol: 'T' },
        { id: 'compression_rate', name: 'Compression Rate', unit: 'm³/s', min: -0.001, max: 0.001, default: 0.0001, symbol: '\\dot{V}' },
    ],
    equations: [
        {
            id: 'boyle',
            name: 'Boyle\'s Law',
            latex: 'PV = nRT = \\text{constant}',
        },
        {
            id: 'isothermal-work',
            name: 'Work Done',
            latex: 'W = nRT\\ln\\frac{V_f}{V_i}',
        },
    ],
    derivatives: (state, params) => {
        const n = getParam(params, 'n');
        const T = getParam(params, 'T');
        const compression_rate = getParam(params, 'compression_rate');
        const V = getState(state, 'V');
        const R = IDEAL_GAS_CONSTANT;
        const P = (n * R * T) / V;
        const dW_dt = P * compression_rate;
        return {
            V: compression_rate,
            P: 0,
            work: dW_dt,
        };
    },
    derivedState: (state, params) => {
        const n = getParam(params, 'n');
        const T = getParam(params, 'T');
        const V = getState(state, 'V');
        const R = IDEAL_GAS_CONSTANT;
        const P = (n * R * T) / V;
        const compression_ratio = V / 0.01;
        return {
            pressure: P,
            compression_ratio,
            boyle_constant: P * V,
        };
    },
    energyComputation: (state, params) => {
        const n = getParam(params, 'n');
        const T = getParam(params, 'T');
        const work = getState(state, 'work');
        const R = IDEAL_GAS_CONSTANT;
        const U = 1.5 * n * R * T;
        const Q = work;
        return {
            internal: U,
            potential: 0,
            kinetic: 0,
            thermal: Q,
            total: U + Q,
        };
    },
    failureModes: [
        {
            id: 'zero-volume',
            name: 'Zero Volume',
            check: (state) => getState(getStateValues(state), 'V') <= 0.0001,
            severity: 'critical',
            educationalMessage: 'Volume has approached zero.',
        },
    ],
    bounds: {
        V: { min: 0.001, max: 0.1 },
        work: { min: -5000, max: 5000 },
    },
});
// ============================================================================
// IDEAL GAS ADIABATIC PROCESS
// ============================================================================
export const IdealGasAdiabatic = createExperimentFromSchema({
    id: 'ideal-gas-adiabatic',
    name: 'Ideal Gas - Adiabatic Process',
    domain: 'physics',
    description: 'Ideal gas undergoing adiabatic (no heat transfer) expansion/compression',
    conceptTags: ['Adiabatic Process', 'Poisson\'s Equation'],
    difficulty: 5,
    stateVariables: [
        { id: 'V', name: 'Volume', unit: 'm³', initial: 0.01, symbol: 'V' },
        { id: 'T', name: 'Temperature', unit: 'K', initial: 300, symbol: 'T' },
        { id: 'P', name: 'Pressure', unit: 'Pa', initial: 0, symbol: 'P' },
    ],
    parameters: [
        { id: 'n', name: 'Moles', unit: 'mol', min: 0.01, max: 10, default: 0.1, symbol: 'n' },
        { id: 'gamma', name: 'Heat Capacity Ratio', unit: '', min: 1.0, max: 1.67, default: 1.4, symbol: '\\gamma' },
        { id: 'compression_rate', name: 'Compression Rate', unit: 'm³/s', min: -0.001, max: 0.001, default: -0.0001, symbol: '\\dot{V}' },
    ],
    equations: [
        {
            id: 'poisson',
            name: 'Poisson\'s Equation',
            latex: 'TV^{\\gamma-1} = \\text{constant}',
        },
        {
            id: 'adiabatic-pressure',
            name: 'Adiabatic Pressure',
            latex: 'PV^{\\gamma} = \\text{constant}',
        },
    ],
    derivatives: (state, params) => {
        const gamma = getParam(params, 'gamma');
        const compression_rate = getParam(params, 'compression_rate');
        const V = getState(state, 'V');
        const T = getState(state, 'T');
        const dV_dt = compression_rate;
        const dT_dt = -(gamma - 1) * T / V * dV_dt;
        return {
            V: dV_dt,
            T: dT_dt,
            P: 0,
        };
    },
    derivedState: (state, params) => {
        const n = getParam(params, 'n');
        const gamma = getParam(params, 'gamma');
        const V = getState(state, 'V');
        const T = getState(state, 'T');
        const R = IDEAL_GAS_CONSTANT;
        const P = (n * R * T) / V;
        const TV_const = T * Math.pow(V, gamma - 1);
        const PV_const = P * Math.pow(V, gamma);
        return {
            pressure: P,
            adiabatic_constant_TV: TV_const,
            adiabatic_constant_PV: PV_const,
            compression_heating: T > 300 ? 1 : 0,
        };
    },
    energyComputation: (state, params) => {
        const n = getParam(params, 'n');
        const gamma = getParam(params, 'gamma');
        const T = getState(state, 'T');
        const R = IDEAL_GAS_CONSTANT;
        const Cv = R / (gamma - 1);
        const U = n * Cv * T;
        return {
            internal: U,
            potential: 0,
            kinetic: 0,
            thermal: 0,
            total: U,
        };
    },
    bounds: {
        V: { min: 0.001, max: 0.1 },
        T: { min: 50, max: 1000 },
    },
});
// ============================================================================
// IDEAL GAS ISOBARIC PROCESS
// ============================================================================
export const IdealGasIsobaric = createExperimentFromSchema({
    id: 'ideal-gas-isobaric',
    name: 'Ideal Gas - Isobaric Process',
    domain: 'physics',
    description: 'Ideal gas undergoing isobaric (constant pressure) expansion/compression',
    conceptTags: ['Isobaric Process', 'Charles\'s Law'],
    difficulty: 3,
    stateVariables: [
        { id: 'V', name: 'Volume', unit: 'm³', initial: 0.01, symbol: 'V' },
        { id: 'T', name: 'Temperature', unit: 'K', initial: 300, symbol: 'T' },
        { id: 'heat_added', name: 'Heat Added', unit: 'J', initial: 0, symbol: 'Q' },
    ],
    parameters: [
        { id: 'n', name: 'Moles', unit: 'mol', min: 0.01, max: 10, default: 0.1, symbol: 'n' },
        { id: 'P', name: 'Pressure', unit: 'Pa', min: 50000, max: 500000, default: 101325, symbol: 'P' },
        { id: 'heating_rate', name: 'Heating Rate', unit: 'K/s', min: -10, max: 10, default: 5, symbol: '\\dot{T}' },
        { id: 'gamma', name: 'Heat Capacity Ratio', unit: '', min: 1.0, max: 1.67, default: 1.4, symbol: '\\gamma' },
    ],
    equations: [
        {
            id: 'charles',
            name: 'Charles\'s Law',
            latex: '\\frac{V}{T} = \\frac{nR}{P} = \\text{constant}',
        },
        {
            id: 'work',
            name: 'Work Done',
            latex: 'W = P\\Delta V',
        },
        {
            id: 'heat',
            name: 'Heat Added',
            latex: 'Q = nC_p\\Delta T',
        },
    ],
    derivatives: (_state, params) => {
        const n = getParam(params, 'n');
        const P = getParam(params, 'P');
        const heating_rate = getParam(params, 'heating_rate');
        const gamma = getParam(params, 'gamma');
        const R = IDEAL_GAS_CONSTANT;
        const dV_dt = (n * R / P) * heating_rate;
        const Cp = (gamma * R) / (gamma - 1);
        const dQ_dt = n * Cp * heating_rate;
        return {
            V: dV_dt,
            T: heating_rate,
            heat_added: dQ_dt,
        };
    },
    derivedState: (state, params) => {
        const n = getParam(params, 'n');
        const P = getParam(params, 'P');
        const gamma = getParam(params, 'gamma');
        const T = getState(state, 'T');
        const heat_added = getState(state, 'heat_added');
        const R = IDEAL_GAS_CONSTANT;
        const V = (n * R * T) / P;
        const Cv = R / (gamma - 1);
        const V0 = 0.01;
        const T0 = 300;
        const work = P * (V - V0);
        const Q = heat_added;
        const delta_U = n * Cv * (T - T0);
        return {
            volume: V,
            work_done: work,
            heat_added: Q,
            delta_U: delta_U,
            first_law_check: Math.abs(Q - delta_U - work) < 1 ? 1 : 0,
        };
    },
    energyComputation: (state, params) => {
        const n = getParam(params, 'n');
        const gamma = getParam(params, 'gamma');
        const T = getState(state, 'T');
        const R = IDEAL_GAS_CONSTANT;
        const Cv = R / (gamma - 1);
        const U = n * Cv * T;
        const Cp = (gamma * R) / (gamma - 1);
        return {
            internal: U,
            potential: 0,
            kinetic: 0,
            thermal: Cp,
            total: U,
        };
    },
    bounds: {
        V: { min: 0.001, max: 0.5 },
        T: { min: 100, max: 1000 },
        heat_added: { min: -5000, max: 5000 },
    },
});
// ============================================================================
// IDEAL GAS ISOCHORIC PROCESS
// ============================================================================
export const IdealGasIsochoric = createExperimentFromSchema({
    id: 'ideal-gas-isochoric',
    name: 'Ideal Gas - Isochoric Process',
    domain: 'physics',
    description: 'Ideal gas undergoing isochoric (constant volume) heating/cooling',
    conceptTags: ['Isochoric Process', 'Gay-Lussac\'s Law'],
    difficulty: 3,
    stateVariables: [
        { id: 'T', name: 'Temperature', unit: 'K', initial: 300, symbol: 'T' },
        { id: 'P', name: 'Pressure', unit: 'Pa', initial: 0, symbol: 'P' },
        { id: 'heat_added', name: 'Heat Added', unit: 'J', initial: 0, symbol: 'Q' },
    ],
    parameters: [
        { id: 'n', name: 'Moles', unit: 'mol', min: 0.01, max: 10, default: 0.1, symbol: 'n' },
        { id: 'V', name: 'Volume', unit: 'm³', min: 0.001, max: 0.1, default: 0.01, symbol: 'V' },
        { id: 'heating_rate', name: 'Heating Rate', unit: 'K/s', min: -10, max: 10, default: 5, symbol: '\\dot{T}' },
        { id: 'gamma', name: 'Heat Capacity Ratio', unit: '', min: 1.0, max: 1.67, default: 1.4, symbol: '\\gamma' },
    ],
    equations: [
        {
            id: 'gay-lussac',
            name: 'Gay-Lussac\'s Law',
            latex: '\\frac{P}{T} = \\frac{nR}{V} = \\text{constant}',
        },
        {
            id: 'no-work',
            name: 'No Work Done',
            latex: 'W = 0',
        },
        {
            id: 'heat',
            name: 'Heat Added',
            latex: 'Q = nC_v\\Delta T',
        },
    ],
    derivatives: (_state, params) => {
        const n = getParam(params, 'n');
        const heating_rate = getParam(params, 'heating_rate');
        const gamma = getParam(params, 'gamma');
        const R = IDEAL_GAS_CONSTANT;
        const Cv = R / (gamma - 1);
        const dQ_dt = n * Cv * heating_rate;
        return {
            T: heating_rate,
            P: 0,
            heat_added: dQ_dt,
        };
    },
    derivedState: (state, params) => {
        const n = getParam(params, 'n');
        const V = getParam(params, 'V');
        const gamma = getParam(params, 'gamma');
        const T = getState(state, 'T');
        const R = IDEAL_GAS_CONSTANT;
        const P = (n * R * T) / V;
        const Cv = R / (gamma - 1);
        const T0 = 300;
        const delta_U = n * Cv * (T - T0);
        return {
            pressure: P,
            work_done: 0,
            delta_U: delta_U,
            Cv: Cv,
        };
    },
    energyComputation: (state, params) => {
        const n = getParam(params, 'n');
        const gamma = getParam(params, 'gamma');
        const T = getState(state, 'T');
        const R = IDEAL_GAS_CONSTANT;
        const Cv = R / (gamma - 1);
        const U = n * Cv * T;
        return {
            internal: U,
            potential: 0,
            kinetic: 0,
            thermal: 0,
            total: U,
        };
    },
    failureModes: [
        {
            id: 'overpressure',
            name: 'Overpressure',
            check: (state) => getState(getStateValues(state), 'P') > 500000,
            severity: 'warning',
            educationalMessage: 'Pressure exceeds 500 kPa.',
        },
    ],
});
// ============================================================================
// CARNOT CYCLE
// ============================================================================
export const CarnotCycle = createExperimentFromSchema({
    id: 'carnot-cycle',
    name: 'Carnot Cycle',
    domain: 'physics',
    description: 'Ideal thermodynamic cycle with maximum possible efficiency',
    conceptTags: ['Carnot Cycle', 'Heat Engine', 'Thermodynamic Efficiency'],
    difficulty: 7,
    stateVariables: [
        { id: 'stage', name: 'Cycle Stage', unit: '', initial: 0, symbol: 'stage' },
        { id: 'V', name: 'Volume', unit: 'm³', initial: 0.01, symbol: 'V' },
        { id: 'T', name: 'Temperature', unit: 'K', initial: 0, symbol: 'T' },
        { id: 'P', name: 'Pressure', unit: 'Pa', initial: 0, symbol: 'P' },
        { id: 'Q_in', name: 'Heat Input', unit: 'J', initial: 0, symbol: 'Q_{in}' },
        { id: 'Q_out', name: 'Heat Rejected', unit: 'J', initial: 0, symbol: 'Q_{out}' },
        { id: 'work_out', name: 'Work Output', unit: 'J', initial: 0, symbol: 'W' },
    ],
    parameters: [
        { id: 'n', name: 'Moles', unit: 'mol', min: 0.01, max: 10, default: 0.1, symbol: 'n' },
        { id: 'T_h', name: 'Hot Reservoir Temp', unit: 'K', min: 300, max: 1000, default: 500, symbol: 'T_H' },
        { id: 'T_c', name: 'Cold Reservoir Temp', unit: 'K', min: 100, max: 400, default: 300, symbol: 'T_C' },
        { id: 'V_a', name: 'Volume at Point A', unit: 'm³', min: 0.001, max: 0.05, default: 0.01, symbol: 'V_a' },
        { id: 'V_b', name: 'Volume at Point B', unit: 'm³', min: 0.01, max: 0.1, default: 0.02, symbol: 'V_b' },
        { id: 'gamma', name: 'Heat Capacity Ratio', unit: '', min: 1.0, max: 1.67, default: 1.4, symbol: '\\gamma' },
        { id: 'cycle_speed', name: 'Cycle Speed', unit: 'stage/s', min: 0.1, max: 2, default: 0.5, symbol: '\\dot{s}' },
    ],
    equations: [
        {
            id: 'efficiency',
            name: 'Carnot Efficiency',
            latex: '\\eta = 1 - \\frac{T_C}{T_H}',
        },
        {
            id: 'entropy',
            name: 'Entropy Change',
            latex: '\\Delta S = \\frac{Q_H}{T_H} - \\frac{Q_C}{T_C} = 0',
        },
    ],
    initialState: (params) => {
        const R = IDEAL_GAS_CONSTANT;
        return {
            stage: 0,
            V: getParam(params, 'V_a'),
            T: getParam(params, 'T_h'),
            P: (getParam(params, 'n') * R * getParam(params, 'T_h')) / getParam(params, 'V_a'),
            Q_in: 0,
            Q_out: 0,
            work_out: 0,
        };
    },
    derivatives: (state, params) => {
        const n = getParam(params, 'n');
        const T_h = getParam(params, 'T_h');
        const T_c = getParam(params, 'T_c');
        const V_b = getParam(params, 'V_b');
        const gamma = getParam(params, 'gamma');
        const R = IDEAL_GAS_CONSTANT;
        const V = getState(state, 'V');
        const T = getState(state, 'T');
        const stage = Math.floor(getState(state, 'stage')) % 4;
        const dV_dt = 0.0001 * (stage === 2 || stage === 3 ? -1 : 1);
        const V_c = Math.pow(Math.pow(V_b, gamma - 1) * T_h / T_c, 1 / (gamma - 1));
        const V_d = Math.pow(Math.pow(V_c, gamma - 1) * T_c / T_h, 1 / (gamma - 1));
        let newStage = stage;
        let dT_dt = 0;
        let dQ_in_dt = 0;
        let dQ_out_dt = 0;
        let dW_dt = 0;
        const P = (n * R * T) / V;
        if (stage === 0) {
            dT_dt = 0;
            dQ_in_dt = P * dV_dt;
            dQ_out_dt = 0;
            dW_dt = P * dV_dt;
            if (V >= V_b) {
                newStage = 1;
            }
        }
        else if (stage === 1) {
            dT_dt = -(gamma - 1) * T / V * dV_dt;
            dQ_in_dt = 0;
            dQ_out_dt = 0;
            dW_dt = P * dV_dt;
            if (T <= T_c) {
                newStage = 2;
            }
        }
        else if (stage === 2) {
            dT_dt = 0;
            dQ_in_dt = 0;
            dQ_out_dt = -P * dV_dt;
            dW_dt = P * dV_dt;
            if (V <= V_d) {
                newStage = 3;
            }
        }
        else {
            dT_dt = -(gamma - 1) * T / V * dV_dt;
            dQ_in_dt = 0;
            dQ_out_dt = 0;
            dW_dt = P * dV_dt;
            if (T >= T_h) {
                newStage = 0;
            }
        }
        return {
            stage: newStage - stage,
            V: dV_dt,
            T: dT_dt,
            P: 0,
            Q_in: dQ_in_dt,
            Q_out: dQ_out_dt,
            work_out: dW_dt,
        };
    },
    derivedState: (state, params) => {
        const T_h = getParam(params, 'T_h');
        const T_c = getParam(params, 'T_c');
        const stage = getState(state, 'stage');
        const Q_in = getState(state, 'Q_in');
        const work_out = getState(state, 'work_out');
        const efficiency = 1 - T_c / T_h;
        const actual_efficiency = Q_in > 0 ? work_out / Q_in : 0;
        const stageIndex = Math.floor(stage) % 4;
        return {
            carnot_efficiency: efficiency,
            actual_efficiency,
            stage_index: stageIndex >= 0 && stageIndex <= 3 ? stageIndex : 0,
            net_work: work_out,
            heat_input: Q_in,
            heat_rejected: getState(state, 'Q_out'),
            thermal_efficiency: actual_efficiency,
        };
    },
    energyComputation: (state, params) => {
        const n = getParam(params, 'n');
        const gamma = getParam(params, 'gamma');
        const T = getState(state, 'T');
        const R = IDEAL_GAS_CONSTANT;
        const Cv = R / (gamma - 1);
        const U = n * Cv * T;
        return {
            internal: U,
            potential: 0,
            kinetic: 0,
            thermal: 0,
            total: U,
        };
    },
    bounds: {
        V: { min: 0.001, max: 0.2 },
        T: { min: 200, max: 600 },
    },
});
// ============================================================================
// HEAT CONDUCTION (1D BAR)
// ============================================================================
export const HeatConduction1D = createExperimentFromSchema({
    id: 'heat-conduction-1d',
    name: '1D Heat Conduction',
    domain: 'physics',
    description: 'Heat conduction through a 1D bar using finite difference method',
    conceptTags: ['Heat Conduction', 'Fourier\'s Law', 'Thermal Diffusion'],
    difficulty: 6,
    stateVariables: [
        { id: 't', name: 'Time', unit: 's', initial: 0, symbol: 't' },
        { id: 'T0', name: 'Temperature x=0', unit: 'K', initial: 300, symbol: 'T_0' },
        { id: 'T1', name: 'Temperature x=L/5', unit: 'K', initial: 300, symbol: 'T_1' },
        { id: 'T2', name: 'Temperature x=2L/5', unit: 'K', initial: 300, symbol: 'T_2' },
        { id: 'T3', name: 'Temperature x=3L/5', unit: 'K', initial: 300, symbol: 'T_3' },
        { id: 'T4', name: 'Temperature x=4L/5', unit: 'K', initial: 300, symbol: 'T_4' },
        { id: 'T5', name: 'Temperature x=L', unit: 'K', initial: 300, symbol: 'T_5' },
        { id: 'heat_flux', name: 'Heat Flux', unit: 'W/m²', initial: 0, symbol: 'q' },
    ],
    parameters: [
        { id: 'T_left', name: 'Left Boundary Temp', unit: 'K', min: 200, max: 500, default: 400, symbol: 'T_L' },
        { id: 'T_right', name: 'Right Boundary Temp', unit: 'K', min: 200, max: 500, default: 300, symbol: 'T_R' },
        { id: 'alpha', name: 'Thermal Diffusivity', unit: 'm²/s', min: 1e-7, max: 1e-4, default: 1e-5, symbol: '\\alpha' },
        { id: 'L', name: 'Bar Length', unit: 'm', min: 0.1, max: 1, default: 0.5, symbol: 'L' },
        { id: 'k_thermal', name: 'Thermal Conductivity', unit: 'W/(m·K)', min: 1, max: 400, default: 200, symbol: 'k' },
    ],
    equations: [
        {
            id: 'heat-eq',
            name: 'Heat Equation',
            latex: '\\frac{\\partial T}{\\partial t} = \\alpha\\frac{\\partial^2 T}{\\partial x^2}',
        },
        {
            id: 'fourier',
            name: 'Fourier\'s Law',
            latex: 'q = -k\\frac{\\partial T}{\\partial x}',
        },
        {
            id: 'diffusion-time',
            name: 'Characteristic Diffusion Time',
            latex: '\\tau = \\frac{L^2}{\\alpha}',
        },
    ],
    derivatives: (state, params) => {
        const T_left = getParam(params, 'T_left');
        const T_right = getParam(params, 'T_right');
        const alpha = getParam(params, 'alpha');
        const L = getParam(params, 'L');
        const dx = L / 5;
        const dx2 = dx * dx;
        const T0 = T_left;
        const T5 = T_right;
        const T1 = getState(state, 'T1');
        const T2 = getState(state, 'T2');
        const T3 = getState(state, 'T3');
        const T4 = getState(state, 'T4');
        const dT1_dt = alpha * (T0 - 2 * T1 + T2) / dx2;
        const dT2_dt = alpha * (T1 - 2 * T2 + T3) / dx2;
        const dT3_dt = alpha * (T2 - 2 * T3 + T4) / dx2;
        const dT4_dt = alpha * (T3 - 2 * T4 + T5) / dx2;
        return {
            t: 1,
            T0: 0,
            T1: dT1_dt,
            T2: dT2_dt,
            T3: dT3_dt,
            T4: dT4_dt,
            T5: 0,
            heat_flux: 0,
        };
    },
    derivedState: (state, params) => {
        const L = getParam(params, 'L');
        const alpha = getParam(params, 'alpha');
        const k_thermal = getParam(params, 'k_thermal');
        const t = getState(state, 't');
        const T0 = getState(state, 'T0');
        const T1 = getState(state, 'T1');
        const T5 = getState(state, 'T5');
        const dx = L / 5;
        const heat_flux = -k_thermal * (T1 - T0) / dx;
        const diffusion_time = (L * L) / alpha;
        const avg_temp = (T0 + T1 + getState(state, 'T2') + getState(state, 'T3') + getState(state, 'T4') + T5) / 6;
        const gradient = (T5 - T0) / L;
        return {
            heat_flux_at_left: heat_flux,
            characteristic_time: diffusion_time,
            progress_fraction: Math.min(1, t / diffusion_time),
            average_temperature: avg_temp,
            temperature_gradient: gradient,
        };
    },
    energyComputation: (state) => {
        const T0 = getState(state, 'T0');
        const T1 = getState(state, 'T1');
        const T2 = getState(state, 'T2');
        const T3 = getState(state, 'T3');
        const T4 = getState(state, 'T4');
        const T5 = getState(state, 'T5');
        const thermal_energy = T0 + T1 + T2 + T3 + T4 + T5;
        return {
            internal: thermal_energy,
            potential: 0,
            kinetic: 0,
            thermal: 0,
            total: thermal_energy,
        };
    },
    bounds: {
        t: { min: 0, max: 10000 },
        T0: { min: 200, max: 500 },
        T1: { min: 200, max: 500 },
        T2: { min: 200, max: 500 },
        T3: { min: 200, max: 500 },
        T4: { min: 200, max: 500 },
        T5: { min: 200, max: 500 },
    },
    failureModes: [
        {
            id: 'steady-state',
            name: 'Steady State Reached',
            check: (state, params) => {
                const L = getParam(params, 'L');
                const stateValues = getStateValues(state);
                const T0 = getState(stateValues, 'T0');
                const T1 = getState(stateValues, 'T1');
                const T4 = getState(stateValues, 'T4');
                const T5 = getState(stateValues, 'T5');
                const dx = L / 5;
                const local_grad_1 = (T1 - T0) / dx;
                const local_grad_4 = (T5 - T4) / dx;
                return Math.abs(local_grad_1 - local_grad_4) < 1;
            },
            severity: 'warning',
            educationalMessage: 'The system has reached steady state.',
        },
    ],
});
//# sourceMappingURL=thermodynamics.js.map